import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, MessageCircle, Loader2, ShieldCheck } from "lucide-react";
import { COMPANY, TIPI_RICAMBIO } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = {
  nome: "",
  codice_motore: "",
  tipo_ricambio: "",
  telefono: "",
  marca_modello: "",
  email: "",
  messaggio: "",
  gdpr: false,
};

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.nome.trim()) return "Inserisci il tuo nome e cognome.";
    if (!form.codice_motore.trim())
      return "Inserisci il codice motore o numero di targa.";
    if (!form.tipo_ricambio) return "Seleziona il tipo di ricambio.";
    if (!form.telefono.trim()) return "Inserisci un numero di telefono.";
    if (!form.marca_modello.trim()) return "Inserisci marca e modello del veicolo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "Inserisci un indirizzo email valido.";
    if (!form.gdpr) return "Devi accettare il trattamento dei dati (GDPR).";
    return null;
  };

  const buildWhatsappLink = () => {
    const lines = [
      `*Nuova richiesta MotorieRicambi*`,
      `Nome: ${form.nome}`,
      `Telefono: ${form.telefono}`,
      form.email ? `Email: ${form.email}` : null,
      `Tipo ricambio: ${form.tipo_ricambio}`,
      `Codice motore/Targa: ${form.codice_motore}`,
      form.marca_modello ? `Marca/Modello: ${form.marca_modello}` : null,
      form.messaggio ? `Messaggio: ${form.messaggio}` : null,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    return `https://wa.me/${COMPANY.whatsappRaw}?text=${text}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quotes`, form, {
        headers: { "Content-Type": "application/json" },
      });
      toast.success(
        "Richiesta inviata! Ti contatteremo entro 24 ore.",
        { duration: 5000 }
      );
      // Apri WhatsApp in parallelo per copia
      window.open(buildWhatsappLink(), "_blank", "noopener");
      setForm(INITIAL);
      return res.data;
    } catch (err2) {
      console.error(err2);
      toast.error(
        "Errore durante l'invio. Apriamo WhatsApp per inviare la richiesta."
      );
      window.open(buildWhatsappLink(), "_blank", "noopener");
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsapp = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    window.open(buildWhatsappLink(), "_blank", "noopener");
  };

  const inputCls =
    "w-full bg-[#141414] border border-white/10 px-4 py-3.5 text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#E63946] transition-colors text-sm";
  const labelCls =
    "block text-xs uppercase tracking-[0.18em] font-mono text-zinc-400 mb-2";

  return (
    <section
      id="preventivo"
      data-testid="quote-form-section"
      className="relative bg-[#0a0a0a] py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#E63946]/15 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#E63946]/10 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-[#E63946]" />
              <span className="text-xs uppercase tracking-[0.3em] font-mono text-[#E63946]">
                / 04 — Preventivo
              </span>
            </div>
            <h2 className="font-heading font-black leading-[0.9] tracking-tighter text-5xl md:text-6xl mb-6">
              Richiedi il<br />
              <span className="text-[#E63946]">Tuo Ricambio.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8">
              Compila il modulo con i dettagli del veicolo. Il nostro team
              tecnico verificherà la disponibilità e ti invierà un preventivo
              personalizzato entro <strong className="text-white">24 ore</strong>.
            </p>

            <ul className="space-y-3 text-sm text-zinc-300">
              {[
                "Preventivo gratuito senza impegno",
                "Risposta tecnica entro 24h",
                "Garanzia 12 mesi su ogni ricambio",
                "Spedizione tracciata in tutta Italia",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <ShieldCheck size={16} className="text-[#E63946]" />
                  {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-8">
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="relative bg-[#0e0e0e] border border-white/10 p-6 md:p-10"
            >
              <div className="absolute -top-px -left-px w-16 h-16 border-t-2 border-l-2 border-[#E63946]" />
              <div className="absolute -bottom-px -right-px w-16 h-16 border-b-2 border-r-2 border-[#E63946]" />

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Nome e Cognome *</label>
                  <input
                    name="nome"
                    value={form.nome}
                    onChange={onChange}
                    data-testid="form-nome"
                    placeholder="Mario Rossi"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Telefono *</label>
                  <input
                    name="telefono"
                    value={form.telefono}
                    onChange={onChange}
                    data-testid="form-telefono"
                    placeholder="+39 ___ _______"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Codice motore o numero targa *
                  </label>
                  <input
                    name="codice_motore"
                    value={form.codice_motore}
                    onChange={onChange}
                    data-testid="form-codice"
                    placeholder="es. CAXA / AB123CD"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Tipo di ricambio *</label>
                  <select
                    name="tipo_ricambio"
                    value={form.tipo_ricambio}
                    onChange={onChange}
                    data-testid="form-tipo"
                    className={inputCls}
                  >
                    <option value="">Seleziona tipo</option>
                    {TIPI_RICAMBIO.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Marca e Modello *</label>
                  <input
                    name="marca_modello"
                    value={form.marca_modello}
                    onChange={onChange}
                    data-testid="form-marca"
                    placeholder="es. Audi A4 2.0 TDI"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="form-email"
                    placeholder="mario@email.it"
                    className={inputCls}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>
                    Messaggio o dettagli aggiuntivi
                  </label>
                  <textarea
                    name="messaggio"
                    value={form.messaggio}
                    onChange={onChange}
                    data-testid="form-messaggio"
                    rows={4}
                    placeholder="Anno, KW, codice telaio, dettagli particolari..."
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <label className="md:col-span-2 flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="gdpr"
                    checked={form.gdpr}
                    onChange={onChange}
                    data-testid="form-gdpr"
                    className="mt-1 w-4 h-4 accent-[#E63946] flex-shrink-0"
                  />
                  <span className="text-xs text-zinc-400 leading-relaxed">
                    Accetto il trattamento dei miei dati personali per ricevere
                    un preventivo, secondo la Privacy Policy e il GDPR. *
                  </span>
                </label>

                <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="form-submit"
                    className="group flex-1 inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#E63946] hover:bg-[#c81d25] disabled:opacity-60 text-white font-bold uppercase tracking-widest text-sm transition-all duration-300"
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Send
                        size={16}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    )}
                    {loading ? "Invio in corso..." : "Invia la tua richiesta"}
                  </button>
                  <button
                    type="button"
                    onClick={sendWhatsapp}
                    data-testid="form-whatsapp"
                    className="inline-flex items-center justify-center gap-3 px-6 py-4 border border-white/20 hover:border-[#25D366] hover:text-[#25D366] text-white font-bold uppercase tracking-widest text-sm transition-all duration-300"
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
